/**
 * TEMPORARY diagnostic for the "An unexpected response was received from the server"
 * Server Action failures.
 *
 * ⚠️ REMOVE THIS FILE (and its call sites) after debugging is complete.
 *
 * Logs:
 *  1. The exact tab origin/port (desync detection)
 *  2. Service Worker registrations/controller (stale SW interception detection)
 *  3. A raw "probe" POST to the current route mimicking a Server Action request,
 *     so we can see the real HTTP status / redirect / content-type / body the
 *     browser actually receives — which the failing action swallows.
 */
export async function diagnoseActionFailure(
  label: string,
  error: unknown
): Promise<void> {
  if (typeof window === "undefined") return;

  const tag = `[ACTION-DIAG:${label}]`;
  const e = error as {
    name?: string;
    message?: string;
    stack?: string;
    digest?: string;
    cause?: unknown;
  };

  console.error(`${tag} ===== Server Action failure =====`);
  console.error(`${tag} location.href =`, window.location.href);
  console.error(`${tag} location.origin =`, window.location.origin);
  console.error(`${tag} navigator.onLine =`, navigator.onLine);
  console.error(`${tag} error.name =`, e?.name);
  console.error(`${tag} error.message =`, e?.message);
  console.error(`${tag} error.digest =`, e?.digest);
  console.error(`${tag} error.cause =`, e?.cause);
  console.error(`${tag} error.stack =`, e?.stack);

  // (A) Service Worker check — stale SW from another app on the same localhost
  //     origin would silently intercept these requests.
  try {
    const sw = navigator.serviceWorker;
    console.error(
      `${tag} SW.controller =`,
      sw?.controller?.scriptURL ?? "none"
    );
    if (sw?.getRegistrations) {
      const regs = await sw.getRegistrations();
      console.error(
        `${tag} SW.registrations =`,
        regs.length
          ? regs.map((r) => ({
              scope: r.scope,
              active: r.active?.scriptURL ?? null,
              waiting: r.waiting?.scriptURL ?? null,
            }))
          : "none"
      );
    }
  } catch (swErr) {
    console.error(`${tag} SW check threw =`, swErr);
  }

  // (B) Probe: POST to the current route like a Server Action would, then dump
  //     the raw response. A bogus Next-Action id is fine — the response
  //     (status / redirect / content-type / body) is what we need.
  try {
    const url = window.location.href;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/x-component",
        "Next-Action": "0000000000000000000000000000000000000000",
        "Content-Type": "text/plain;charset=UTF-8",
      },
      body: "[]",
    });
    console.error(`${tag} probe.status =`, res.status, res.statusText);
    console.error(`${tag} probe.type =`, res.type);
    console.error(`${tag} probe.redirected =`, res.redirected, "→", res.url);
    console.error(
      `${tag} probe.content-type =`,
      res.headers.get("content-type")
    );
    console.error(
      `${tag} probe.x-middleware =`,
      res.headers.get("x-middleware-rewrite") ||
        res.headers.get("location") ||
        "(none)"
    );
    const text = await res.text();
    console.error(`${tag} probe.body.length =`, text.length);
    console.error(`${tag} probe.body[0:800] =`, text.slice(0, 800));
  } catch (probeErr) {
    const pe = probeErr as { name?: string; message?: string };
    console.error(`${tag} probe.fetch THREW =`, pe?.name, pe?.message);
  }

  console.error(`${tag} ===== end =====`);
}
